import { useCallback, useEffect, useState } from "react";

export type Route =
  | "recent"
  | "calendar"
  | "schedule"
  | "forum"
  | "documents"
  | "audit"
  | "users";

export interface RouterState {
  route: Route;
  selectedDate: Date; // only meaningful when route === "calendar"
  focusImageId: string | null; // only meaningful when route === "calendar"
}

/**
 * Hash-based router. Format:
 *   #/recent
 *   #/calendar
 *   #/calendar?d=2026-05-16
 *   #/calendar?d=2026-05-16&img=<uuid>
 *   #/schedule
 *   #/forum
 *   #/documents
 *   #/audit
 *   #/users
 *
 * Only the Calendar carries parameters; the rest are bare paths. Bigger than
 * rolling-our-own URL parsing, smaller than pulling react-router in.
 */
export function useRoute(): {
  state: RouterState;
  go: (route: Route, opts?: { date?: Date; imageId?: string | null }) => void;
  hashFor: (route: Route, opts?: { date?: Date; imageId?: string | null }) => string;
} {
  const [state, setState] = useState<RouterState>(parseHash);

  useEffect(() => {
    const onHash = () => setState(parseHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const go = useCallback(
    (route: Route, opts?: { date?: Date; imageId?: string | null }) => {
      const hash = serialize(route, opts?.date ?? null, opts?.imageId ?? null);
      if (window.location.hash !== hash) {
        window.location.hash = hash;
      } else {
        // No-op hash change still updates state (e.g. clicking Recent twice).
        setState({
          route,
          selectedDate: opts?.date ?? new Date(),
          focusImageId: opts?.imageId ?? null,
        });
      }
    },
    [],
  );

  const hashFor = useCallback(
    (route: Route, opts?: { date?: Date; imageId?: string | null }) =>
      serialize(route, opts?.date ?? null, opts?.imageId ?? null),
    [],
  );

  return { state, go, hashFor };
}

function parseHash(): RouterState {
  const raw = window.location.hash.replace(/^#\/?/, "");
  const [path, query] = raw.split("?");
  const route: Route = isRoute(path) ? path : "recent";
  let selectedDate = new Date();
  let focusImageId: string | null = null;
  if (query) {
    const params = new URLSearchParams(query);
    const d = params.get("d");
    if (d) {
      const parsed = parseLocalDate(d);
      if (parsed && !Number.isNaN(parsed.getTime())) selectedDate = parsed;
    }
    const img = params.get("img");
    if (img) focusImageId = img;
  }
  return { route, selectedDate, focusImageId };
}

const ROUTES: readonly Route[] = [
  "recent",
  "calendar",
  "schedule",
  "forum",
  "documents",
  "audit",
  "users",
];

function isRoute(path: string): path is Route {
  return (ROUTES as readonly string[]).includes(path);
}

function serialize(route: Route, date: Date | null, imageId: string | null): string {
  if (route !== "calendar") return `#/${route}`;
  const params = new URLSearchParams();
  if (date) params.set("d", formatLocalDate(date));
  if (imageId) params.set("img", imageId);
  const qs = params.toString();
  return qs ? `#/calendar?${qs}` : "#/calendar";
}

// Format/parse using local calendar parts so a YYYY-MM-DD round-trip preserves
// the user's clicked day. `toISOString()` would shift to UTC and the parse-back
// would land on a different local day for any non-UTC timezone.
function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseLocalDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
